import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import CtaBand from "@/components/site/CtaBand";
import RelatedLinks from "@/components/site/RelatedLinks";
import Breadcrumbs, { breadcrumbJsonLd } from "@/components/site/Breadcrumbs";
import { COLLECTIONS, findPage, pageUrl } from "@/data/seoPages";
import { useSeo } from "@/lib/useSeo";

const SITE = "https://anderstongroup.co.uk";
const ORG = { "@id": `${SITE}/#organization` };

const serviceJsonLd = (collection, page, path) => ({
  "@type": "Service",
  "@id": `${SITE}${path}#service`,
  name: collection === "locations" ? `Interior fit-out — ${page.name}` : page.name,
  serviceType: collection === "locations" ? "Interior fit-out contractor" : page.name,
  description: page.description,
  url: `${SITE}${path}`,
  image: `${SITE}${page.image}`,
  provider: ORG,
  areaServed:
    collection === "locations"
      ? { "@type": "Place", name: `${page.name}, Scotland` }
      : { "@type": "Country", name: "United Kingdom" },
});

const faqJsonLd = (faqs) => ({
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export default function SeoPage({ collection }) {
  const { slug } = useParams();
  const meta = COLLECTIONS[collection];
  const page = findPage(collection, slug);
  const path = page ? pageUrl(collection, slug) : meta.base;

  const crumbs = useMemo(
    () => [
      { name: "Home", to: "/" },
      { name: meta.label, to: meta.base },
      ...(page ? [{ name: page.name, to: path }] : []),
    ],
    [meta, page, path]
  );

  const seo = useMemo(
    () =>
      page
        ? {
            title: page.title,
            description: page.description,
            path,
            image: page.image,
            jsonLd: {
              "@context": "https://schema.org",
              "@graph": [
                serviceJsonLd(collection, page, path),
                breadcrumbJsonLd(crumbs),
                ...(page.faqs?.length ? [faqJsonLd(page.faqs)] : []),
              ],
            },
          }
        : { title: "Page not found | Anderston Group", description: meta.description, path: meta.base },
    [page, path, collection, crumbs, meta]
  );
  useSeo(seo);

  return (
    <main data-testid={`${collection}-page`} className="min-h-screen bg-white text-[#0A0A0A]">
      <Navbar />
      <article className="pt-[9rem] lg:pt-[11rem]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 lg:pb-28">
          <Breadcrumbs items={crumbs} />

          {!page && (
            <div data-testid="seo-page-missing" className="py-24 border-t hairline mt-10">
              <div className="overline">404</div>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight">That page isn't here.</h1>
              <Link to={meta.base} className="mt-8 inline-flex items-center px-6 py-3.5 text-sm font-medium bg-[#0A0A0A] text-white">
                Back to {meta.label.toLowerCase()}
              </Link>
            </div>
          )}

          {page && (
            <>
              {/* Header */}
              <header className="mt-10 lg:mt-14 grid grid-cols-12 gap-y-6 lg:gap-x-10">
                <div className="col-span-12 lg:col-span-8">
                  <div className="overline">{meta.label} — {page.name}</div>
                  <h1
                    data-testid="seo-page-title"
                    className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.025em] leading-[1.0] text-balance"
                  >
                    {page.h1}
                  </h1>
                </div>
                <div className="col-span-12 lg:col-span-4 lg:pt-16">
                  <p data-testid="seo-page-intro" className="text-base lg:text-lg text-black/65 leading-relaxed">
                    {page.intro}
                  </p>
                  <Link
                    to="/#contact"
                    data-testid="seo-page-cta"
                    className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 text-sm font-medium bg-[#0A0A0A] text-white hover:bg-black/85 transition-colors"
                  >
                    Start a project <ArrowUpRight size={16} />
                  </Link>
                </div>
              </header>

              <div className="mt-12 lg:mt-16 aspect-[16/9] lg:aspect-[21/9] overflow-hidden">
                <img src={page.image} alt={`${page.name} — Anderston Group interior fit-out`} className="w-full h-full object-cover" loading="eager" />
              </div>

              {/* Body */}
              <div className="mt-16 lg:mt-24 grid grid-cols-12 gap-y-10 lg:gap-x-16">
                <div className="col-span-12 lg:col-span-7">
                  {page.sections.map((s, i) => (
                    <section key={s.heading} className={i > 0 ? "mt-12" : ""}>
                      <h2 className="font-display text-2xl lg:text-3xl tracking-tight">{s.heading}</h2>
                      <p className="mt-4 text-black/70 leading-relaxed">{s.body}</p>
                    </section>
                  ))}
                </div>
                <aside className="col-span-12 lg:col-span-4 lg:col-start-9">
                  <div className="border hairline p-7 lg:p-8 lg:sticky lg:top-40">
                    <div className="overline">{page.bullets.heading}</div>
                    <ul data-testid="seo-page-bullets" className="mt-5 space-y-3">
                      {page.bullets.items.map((b) => (
                        <li key={b} className="flex gap-3 text-sm text-black/75">
                          <span className="mt-2.5 w-3 h-px bg-black shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </aside>
              </div>

              {/* FAQs */}
              {page.faqs?.length > 0 && (
                <section data-testid="seo-page-faqs" className="mt-16 lg:mt-24 border-t hairline pt-10 grid grid-cols-12 gap-y-6 lg:gap-x-8">
                  <div className="col-span-12 lg:col-span-3">
                    <div className="overline">Questions</div>
                  </div>
                  <dl className="col-span-12 lg:col-span-8">
                    {page.faqs.map((f) => (
                      <div key={f.q} className="py-6 border-b hairline first:pt-0">
                        <dt className="font-display text-xl lg:text-2xl tracking-tight">{f.q}</dt>
                        <dd className="mt-3 text-black/65 leading-relaxed">{f.a}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              )}

              <RelatedLinks related={page.related} />
            </>
          )}
        </div>
      </article>
      <CtaBand copy="Briefs, drawings, a paragraph on a napkin — we'll come back with a feasibility view." />
      <Footer />
    </main>
  );
}
